import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '@/hooks/use-app-store';
import { useAudioRecorder } from '@/hooks/use-audio-recorder';

type ChatMessage = {
  role: string;
  content: string;
  partial?: boolean;
  id?: string;
};


export const useDEAIChat = () => {
  // --- State Management ---
  const [messageInput, setMessageInput] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [timerLabel, setTimerLabel] = useState("00:00");
  const stopTimerRef = useRef(null);

  // --- External Hooks ---
  const { isTyping, sendMessage, sub, unsub } = useAppStore(state => state);
  const { isRecording, startRecording, stopRecording, isInitializing } = useAudioRecorder();

  const handleMessage = useCallback((payload?: object) => {
    const { id, content, role } = payload as { id: string; content: string; role: string };
    if (role === 'assistant') {
      const existingMessageIndex = chatMessages.findIndex(
        (msg) => msg.role === role && msg.id === id
      );

      if (existingMessageIndex > -1) {
        const newMessages = [...chatMessages];
        newMessages[existingMessageIndex].content += content;
        return newMessages;
      }
    }
    setChatMessages( [...chatMessages, { id, content, role }]);
  }, [chatMessages, setChatMessages]);

  const handleChatlogClear = useCallback(() => {
    setChatMessages([]);
  }, []);

  // --- Effects for Lifecycle and Subscriptions ---
  useEffect(() => {
    sub('message', handleMessage);
    sub('chatlog_clear', handleChatlogClear);
    return () => {
      unsub('message', handleMessage);
      unsub('chatlog_clear', handleChatlogClear);
    };
  }, [sub, unsub, handleMessage, handleChatlogClear]);

  // --- User Action Handlers ---
  const sendUserMessage = useCallback(() => {
    const content = messageInput.trim();
    if (!content) return;

    const userMessage = {
      id: `user_${Date.now()}`,
      action: "message",
      role: "user",
      content,
    };
    sendMessage(userMessage);
    setChatMessages((prev) => [...prev, userMessage]); // Optimistic update
    setMessageInput("");
  }, [messageInput, sendMessage]);

  // --- Props Getters for UI Components ---
  // This pattern simplifies passing props to the UI
  const getInputProps = useCallback(() => ({
    type: "text",
    placeholder: "Type your message here...",
    autoComplete: "off",
    value: messageInput,
    onChange: (e) => setMessageInput(e.target.value),
    onKeyDown: (e) => { if (e.key === 'Enter') sendUserMessage(); },
  }), [messageInput, sendUserMessage]);

  const getSendButtonProps = useCallback(() => ({
    onClick: sendUserMessage,
    disabled: messageInput.trim() === "",
    'aria-label': 'Send message',
  }), [messageInput, sendUserMessage]);
  
  const getMicButtonProps = useCallback((props = {}) => ({
    ...props,
  }), [isInitializing, isRecording, timerLabel]);
  
  // --- Returned values for the UI component ---
  return {
    chatMessages,
    isTyping,
    isRecording,
    isInitializing,
    timerLabel,
    getInputProps,
    getSendButtonProps,
    getMicButtonProps,
  };
};